import type { TDocumentDefinitions, Content } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import type { GeneratedLineList } from "../../../domain/inspection-book/line-list.js";
import {
  buildLineListTable,
  buildLineListReferencesBlock,
  buildLineListFailureBlock,
  resolveLineListLayout,
  lineListPageMargins,
  LINE_LIST_PAGE_SIZE,
  LINE_LIST_PAGE_ORIENTATION
} from "./line-list-render.js";
import type { LineListLayoutResult } from "./line-list-layout.js";
import { formatReportValue } from "../../../shared/format/report-value.js";
import type { LineListPdfContext } from "../../../application/services/piping-system-line-list-service.js";

/**
 * PDF INDEPENDENTE DA LISTA DE LINHAS — documento próprio, gerado pela
 * renderização oficial do INSPECT IA (pdfmake), nunca uma captura de tela.
 * Consome exatamente o mesmo `GeneratedLineList` usado pelo Book e pelo RI
 * (fonte única — comando item 29): mesma tabela, mesma ordem, mesmos dados
 * (item 19). O cabeçalho e a identificação seguem o item 18 do comando.
 */
export class LineListBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const data = snapshot.data || {};
    const list: GeneratedLineList = data.generatedLineList;
    const pdfContext = data.pdfContext || {};
    // Autoajuste (comando "autoajuste inteligente"): resolvido uma única vez
    // e reaproveitado para a margem lateral da página e para a tabela —
    // mesmo motor de layout usado pela Lista de Linhas dentro do Book, para
    // que o PDF independente e o Book nunca divirjam (mesmo Document Model).
    const layout: LineListLayoutResult | null = list && list.totalLines > 0 ? resolveLineListLayout(list) : null;
    const sideMarginPt = layout && layout.ok ? layout.sideMarginPt : 22.68;
    const [marginLeft, , marginRight, marginBottom] = lineListPageMargins(sideMarginPt);

    return {
      // A3 PAISAGEM obrigatório para o PDF independente (comando "autoajuste
      // inteligente", item 24).
      pageSize: LINE_LIST_PAGE_SIZE,
      pageOrientation: LINE_LIST_PAGE_ORIENTATION,
      // Topo maior (90) para acomodar o cabeçalho de marca próprio deste PDF
      // independente (diferente da seção dentro do Book).
      pageMargins: [marginLeft, 90, marginRight, marginBottom],
      header: (currentPage: number) => {
        if (currentPage !== 1) {
          return {
            margin: [36, 16, 36, 0],
            columns: [
              { text: 'INSPECT IA — LISTA DE LINHAS', style: 'brandSubtitle' },
              { text: formatReportValue(list?.systemTag), style: 'brandSubtitle', alignment: 'right' }
            ]
          };
        }
        return { text: '' };
      },
      footer: (currentPage: number, pageCount: number, pageSize) => ({
        margin: [36, 12, 36, 0],
        stack: [
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: pageSize.width - 72, y2: 0, lineWidth: 0.5, lineColor: '#94A3B8' }], margin: [0, 0, 0, 4] },
          {
            columns: [
              { text: 'INSPECT IA — Lista de Linhas', style: 'footerText' },
              { text: `Página ${currentPage} de ${pageCount}`, style: 'footerText', alignment: 'right' }
            ]
          }
        ]
      }),
      content: this.buildContent(list, layout, pdfContext),
      styles: {
        brandTitle: { fontSize: 14, bold: true, color: '#0F172A' },
        brandSubtitle: { fontSize: 8, color: '#64748B' },
        docTitle: { fontSize: 16, bold: true, color: '#0F172A', margin: [0, 4, 0, 14] },
        footerText: { fontSize: 8, color: '#64748B' },
        tableHeader: { fontSize: 8, bold: true, color: '#FFFFFF', fillColor: '#374151', alignment: 'left' },
        tableCell: { fontSize: 8, color: '#334155' },
        boldCell: { fontSize: 8, bold: true, color: '#0F172A' },
      },
      defaultStyle: { fontSize: 8, font: 'Roboto' }
    };
  }

  private buildContent(list: GeneratedLineList, layout: LineListLayoutResult | null, pdfContext: LineListPdfContext): Content[] {
    const content: Content[] = [
      { text: 'INSPECT IA', style: 'brandTitle' },
      { text: 'LISTA DE LINHAS', style: 'docTitle' },
      this.buildIdentificationTable(list, pdfContext)
    ];

    if (!list || list.totalLines === 0) {
      content.push({
        text: 'SISTEMA SEM LINHAS CADASTRADAS — o Sistema de Tubulação não possui Linhas vinculadas no Cadastro de Equipamentos. Cadastre ou importe as Linhas antes de gerar a Lista de Linhas.',
        style: 'tableCell',
        margin: [0, 10, 0, 0]
      });
      return content;
    }

    if (!layout || !layout.ok) {
      content.push(buildLineListFailureBlock(
        layout && !layout.ok
          ? layout
          : { ok: false, message: 'Não foi possível gerar a Lista de Linhas mantendo os critérios mínimos de legibilidade e integridade dos dados.', offendingColumns: [], shortfallPt: 0 }
      ));
      return content;
    }

    content.push({ text: '', margin: [0, 4, 0, 0] });
    content.push(buildLineListTable(list, layout.widths));
    const references = buildLineListReferencesBlock(list);
    if (references) content.push(references);
    return content;
  }

  private buildIdentificationTable(list: GeneratedLineList, pdfContext: LineListPdfContext): Content {
    const row = (label: string, value: unknown) => [{ text: label, style: 'tableHeader' }, { text: formatReportValue(value), style: 'tableCell' }];
    return {
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [...row('CLIENTE:', pdfContext.clientLabel), ...row('OS:', pdfContext.osLabel)],
          [...row('PROJETO:', pdfContext.projectLabel), ...row('UNIDADE / ÁREA:', pdfContext.unitAreaLabel)],
          [...row('SISTEMA DE TUBULAÇÃO:', list?.systemTag), ...row('DESCRIÇÃO / SERVIÇO:', list?.systemDescription)],
          [{ text: 'DATA DE GERAÇÃO:', style: 'tableHeader' }, { text: list ? new Date(list.generatedAt).toLocaleDateString('pt-BR') : formatReportValue(null), style: 'tableCell' },
           { text: 'TOTAL DE LINHAS:', style: 'tableHeader' }, { text: String(list?.totalLines ?? 0), style: 'boldCell' }],
        ]
      },
      layout: {
        hLineWidth: () => 0.5, vLineWidth: () => 0.5,
        hLineColor: () => '#E2E8F0', vLineColor: () => '#E2E8F0',
        paddingLeft: () => 6, paddingRight: () => 6, paddingTop: () => 4, paddingBottom: () => 4,
      },
      margin: [0, 0, 0, 14]
    };
  }
}

export const lineListBuilder = new LineListBuilder();
documentEngine.registerBuilder('LINE_LIST', lineListBuilder);
